/* eslint-disable camelcase */
/**
 * TP/SL trigger outcome notifications (`tpsl_executed` / `tpsl_rejected`).
 *
 * Emitted by the backend's POST /tpsl/webhook when the off-chain trigger
 * executor fires (or fails to fire) a take-profit / stop-loss.
 *
 * Every numeric field in the metadata is ALREADY a human decimal string — the
 * backend converts the executor's fixed-18 integers with formatFixed18 before
 * persisting. This renderer therefore only groups digits; it must never scale.
 */
import { useMemo } from "react";

import { NotificationTypeResponse } from "../../../codegen-api";
import { TEXT_COLORS } from "../../../constants/design/colors";
import { getAssetShortName } from "../../../utils/asset/assets";
import { getTimeAgo } from "../../../utils/date";
import {
  formatHumanDecimal,
  formatHumanPrice,
} from "../../../utils/notifications";
import { Pill } from "../../../pages/PortfolioPage/mobile/style";
import TickerLogo from "../TickerLogo";
import {
  Content,
  FillNotificationTitle,
  IconWrapper,
  NotificationWrapper,
  PillContent,
  ReadIndicator,
  Timestamp,
  Title,
} from "./style";
import { activityMoment, getInstrumentBaseAsset } from "./helpers";
import { INotificationProps } from "./types";

/** "0" is the executor's sentinel for "close the whole position". */
const FULL_CLOSE_SENTINEL = "0";

export function TpslNotification({
  notification,
  t,
  translateAll,
}: INotificationProps) {
  const { metadata } = notification;
  const executed =
    notification.notification_type === NotificationTypeResponse.TpslExecuted;
  const isTakeProfit = metadata?.trigger_type === "take_profit";

  // `instrument` and `instrument_name` carry the same value; either may be the
  // one a given emitter set.
  const instrument = String(
    metadata?.instrument_name || metadata?.instrument || ""
  ).toUpperCase();
  const asset = metadata?.asset || getInstrumentBaseAsset(instrument);
  const assetLabel = getAssetShortName(asset) || asset || "";

  // `is_long` is authoritative; `side` is the same fact in the positions API's
  // buy/sell vocabulary and is the fallback when the boolean is missing.
  const isLong =
    typeof metadata?.is_long === "boolean"
      ? metadata.is_long
      : metadata?.side !== "sell";

  const triggerPrice = formatHumanPrice(metadata?.trigger_price);
  const executedPrice = formatHumanPrice(metadata?.executed_price);

  const sizeLabel = useMemo(() => {
    const raw = metadata?.size_in_tokens;
    if (raw === undefined || raw === null || String(raw) === FULL_CLOSE_SENTINEL) {
      return t("tpsl_full_close", { defaultValue: "Full close" });
    }
    // Base-token sizes are small; keep more decimals than a price.
    const size = formatHumanDecimal(raw, 8);
    if (!size) return t("tpsl_full_close", { defaultValue: "Full close" });
    return assetLabel ? `${size} ${assetLabel}` : size;
  }, [assetLabel, metadata?.size_in_tokens, t]);

  const title = useMemo(() => {
    if (isTakeProfit) {
      return executed
        ? t("tpsl_take_profit_executed", {
            defaultValue: "Take Profit triggered",
          })
        : t("tpsl_take_profit_failed", { defaultValue: "Take Profit failed" });
    }
    return executed
      ? t("tpsl_stop_loss_executed", { defaultValue: "Stop Loss triggered" })
      : t("tpsl_stop_loss_failed", { defaultValue: "Stop Loss failed" });
  }, [executed, isTakeProfit, t]);

  const direction = isLong
    ? t("tpsl_long", { defaultValue: "long" })
    : t("tpsl_short", { defaultValue: "short" });

  const body = useMemo(() => {
    const market = instrument || assetLabel;
    const trigger = triggerPrice ?? "-";

    if (!executed) {
      return t("tpsl_rejected_desc", {
        defaultValue: "{{instrument}} {{direction}} close failed (trigger {{trigger}})",
        instrument: market,
        direction,
        trigger,
      });
    }

    // A trigger can execute without the executor reporting a fill price.
    if (!executedPrice) {
      return t("tpsl_executed_desc_no_price", {
        defaultValue: "{{instrument}} {{direction}} closed (trigger {{trigger}})",
        instrument: market,
        direction,
        trigger,
      });
    }

    return t("tpsl_executed_desc", {
      defaultValue:
        "{{instrument}} {{direction}} closed @ {{price}} (trigger {{trigger}})",
      instrument: market,
      direction,
      price: executedPrice,
      trigger,
    });
  }, [
    assetLabel,
    direction,
    executed,
    executedPrice,
    instrument,
    t,
    triggerPrice,
  ]);

  return (
    <NotificationWrapper isRead={notification.is_read}>
      <IconWrapper>
        <TickerLogo asset={asset} instrumentName={instrument} size={32} />
        {!notification.is_read && <ReadIndicator />}
      </IconWrapper>
      <div>
        <Title>
          <FillNotificationTitle>
            <span>{title}</span>
          </FillNotificationTitle>
          <Pill $isPositive={executed} $borderRadius="4px">
            <PillContent>{sizeLabel}</PillContent>
          </Pill>
        </Title>
        <Content $color={TEXT_COLORS.two}>{body}</Content>
        <Timestamp>
          {getTimeAgo(activityMoment(notification), translateAll)}
        </Timestamp>
      </div>
    </NotificationWrapper>
  );
}

export default TpslNotification;
